import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { updateProductScreen, getTexCard, updateTimer } from '../../services/productService';

function ModifiersPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [modifiers, setModifiers] = useState([]);
  const [techCards, setTechCards] = useState([]);
  const [screens, setScreens] = useState([]);
  const [showTimerPopup, setShowTimerPopup] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newTimerMinutes, setNewTimerMinutes] = useState('');
  const [newTimerSeconds, setNewTimerSeconds] = useState('');
  const [productName, setProductName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getTexCard(productId)
      .then((data) => {
        setModifiers(data.items.modifiers);
        setTechCards(data.items.tech_cards);
        setProductName(data.items.product.name);
        setScreens(data.screens);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Cannot get data:', err);
        setLoading(false);
      });
  }, [productId]);

  const handleScreenToggle = useCallback(async (id, screenId, currentIsActive, type) => {
    try {
      const toggleState = (prev) =>
        prev.map((item) => {
          if (item.id === id) {
            return {
              ...item,
              product_screens: currentIsActive
                ? item.product_screens.filter((ps) => ps.screen.id !== screenId)
                : [
                    ...item.product_screens,
                    {
                      screen: {
                        id: screenId,
                        name: screens.find((screen) => screen.id === screenId)?.name,
                        description: 'screen',
                        created_at: new Date().toISOString(),
                      },
                      is_active: true,
                      created_at: new Date().toISOString(),
                    },
                  ],
            };
          }
          return item;
        });

      if (type === 'modifier') {
        setModifiers(toggleState);
      } else {
        setTechCards(toggleState);
      }

      await updateProductScreen(id, screenId, !currentIsActive);
    } catch (error) {
      console.error('Cannot update', error);
    }
  }, [screens]);

  const handleTimerClick = useCallback((item, type) => {
    setSelectedItem({ ...item, type });
    const [, minutes, seconds] = item.timer
      ? item.timer.split(':').map(Number)
      : [0, 0, 0];
    setNewTimerMinutes(String(minutes));
    setNewTimerSeconds(String(seconds));
    setShowTimerPopup(true);
  }, []);

  const handleTimerUpdate = useCallback(async () => {
    if (!selectedItem) return;

    try {
      const timerValue = `00:${newTimerMinutes.padStart(2, '0')}:${newTimerSeconds.padStart(2, '0')}`;
      await updateTimer(selectedItem.id, timerValue);

      const updateState = (prev) =>
        prev.map((item) =>
          item.id === selectedItem.id
            ? { ...item, timer: timerValue }
            : item
        );

      if (selectedItem.type === 'modifier') {
        setModifiers(updateState);
      } else {
        setTechCards(updateState);
      }

      setShowTimerPopup(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Cannot update timer', error);
    }
  }, [selectedItem, newTimerMinutes, newTimerSeconds]);

  const handlePopupClose = useCallback(() => {
    setShowTimerPopup(false);
    setSelectedItem(null);
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 text-gray-500">
        <div className="w-12 h-12 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
        <p className="font-medium">Загрузка...</p>
      </div>
    );
  }

  const renderTable = (items, title, type, nameLabel) => (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">{title}</h2>
      {items.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
          Нет данных
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200">
                  {nameLabel}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 w-24">
                  Таймер
                </th>
                {screens.map((screen) => (
                  <th key={screen.id} className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 w-20">
                    {screen.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 border-b border-gray-100 text-sm text-gray-700 font-medium">
                    {item.name}
                  </td>
                  <td className="px-4 py-3 border-b border-gray-100 text-center">
                    <button
                      onClick={() => handleTimerClick(item, type)}
                      className="px-2.5 py-1 text-xs font-semibold text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
                    >
                      ⏱️ {item.timer ? item.timer.slice(3) : '00:00'}
                    </button>
                  </td>
                  {screens.map((screen) => {
                    const isActive = item.product_screens.some(
                      (ps) => ps.screen.id === screen.id && ps.is_active
                    );
                    return (
                      <td key={screen.id} className="px-4 py-3 border-b border-gray-100 text-center">
                        <label className="inline-flex items-center justify-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={() => handleScreenToggle(item.id, screen.id, isActive, type)}
                            className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                          />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Back button */}
      <button 
        onClick={() => navigate('/products')}
        className="inline-flex items-center gap-1 px-3 py-2 mb-4 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
      >
        ← Назад к продуктам
      </button>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">🍔 {productName}</h1>
        <p className="text-gray-500">Управление модификаторами и техкартами</p>
      </div>

      {/* Modifiers Table */}
      {renderTable(modifiers, '🔧 Модификаторы', 'modifier', 'Название модификатора')}

      {/* Tech Cards Table */}
      {renderTable(techCards, '📋 Техкарты', 'techCard', 'Название техкарты')}

      {/* Timer Popup */}
      {showTimerPopup && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={handlePopupClose}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-[90%] p-6 animate-[slideUp_0.3s_ease]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">⏱️ Установить таймер</h2>
              <p className="text-gray-500">{selectedItem?.name}</p>
            </div>
            
            <div className="flex items-end justify-center gap-2 mb-6">
              <div className="text-center">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Минуты</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={newTimerMinutes}
                  onChange={(e) => setNewTimerMinutes(e.target.value)}
                  className="w-20 px-3 py-3 text-2xl font-semibold text-center bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:bg-white focus:outline-none transition-colors"
                />
              </div>
              <span className="text-2xl font-semibold text-gray-400 pb-3">:</span>
              <div className="text-center">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Секунды</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={newTimerSeconds}
                  onChange={(e) => setNewTimerSeconds(e.target.value)}
                  className="w-20 px-3 py-3 text-2xl font-semibold text-center bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:bg-white focus:outline-none transition-colors"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={handlePopupClose}
                className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Отмена
              </button>
              <button 
                onClick={handleTimerUpdate}
                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                Обновить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModifiersPage;
